import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { Box, Typography, TextField, Button, CircularProgress, Divider, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, ErrorOutline } from '@mui/icons-material';
import { GoogleIcon } from '../../components/CustomIcons';
import logo from '../../images/logo.svg';
import { colors, typography, radius, inputSx, btnPrimary } from '../../theme';

const BENEFITS = [
    { icon: '✂️', title: 'Personal Dashboard',   desc: 'Manage your own queue and appointments — all in one clean view.' },
    { icon: '🔔', title: 'Availability Toggle',   desc: 'Switch between available and busy with a single tap, any time.' },
    { icon: '🗓️', title: 'Daily Schedule',        desc: "See today's appointments and walk-ins in one organised list." },
    { icon: '⭐', title: 'Build Your Reputation', desc: 'Collect reviews and grow your name in the local barber community.' },
];

// ── subtle animated background dots ──────────────────────────────────────────
const ScissorsBg = () => (
    <Box sx={{
        position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0,
        opacity: 0.04,
    }}>
        {[...Array(12)].map((_, i) => (
            <Box key={i} sx={{
                position: 'absolute',
                fontSize: `${20 + (i % 4) * 14}px`,
                top:  `${(i * 37 + 11) % 110 - 5}%`,
                left: `${(i * 53 + 7)  % 110 - 5}%`,
                transform: `rotate(${i * 29}deg)`,
                color: colors.primary,
                userSelect: 'none',
            }}>✂</Box>
        ))}
    </Box>
);

const JoinShopPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const benefitsRef = useRef(null);

    const [codeInput,   setCodeInput]   = useState(searchParams.get('code') ?? '');
    const [invite,      setInvite]      = useState(null);
    const [codeError,   setCodeError]   = useState('');
    const [codeLoading, setCodeLoading] = useState(false);
    const [step,        setStep]        = useState(1);
    const [showPass,    setShowPass]    = useState(false);
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState('');

    useEffect(() => {
        const urlCode = searchParams.get('code');
        if (urlCode) { setCodeInput(urlCode); verifyCode(urlCode); }
    }, []);

    const verifyCode = async (code = codeInput) => {
        if (!code.trim()) { setCodeError('Please enter an invite code.'); return; }
        setCodeLoading(true); setCodeError('');
        try {
            const q    = query(collection(db, 'invites'), where('code', '==', code.trim().toUpperCase()));
            const snap = await getDocs(q);
            if (snap.empty) { setCodeError('Invalid invite code. Please check and try again.'); setCodeLoading(false); return; }
            const inviteDoc = { id: snap.docs[0].id, ...snap.docs[0].data() };
            if (inviteDoc.used) { setCodeError('This invite code has already been used.'); setCodeLoading(false); return; }
            if (inviteDoc.expiresAt.toDate() < new Date()) { setCodeError('This invite code has expired.'); setCodeLoading(false); return; }
            setInvite(inviteDoc); setStep(2);
        } catch (e) { console.error(e); setCodeError('Failed to verify code. Try again.'); }
        setCodeLoading(false);
    };

    const completeSignup = async (user, name) => {
        const barberRef = await addDoc(collection(db, 'shops', invite.shopId, 'barbers'), {
            name, email: user.email, image: user.photoURL || '', experience: 0,
            specialty: [], rating: 0, waitingList: [], isAvailable: true,
            userId: user.uid, createdAt: serverTimestamp(),
        });
        await setDoc(doc(db, 'users', user.uid), {
            email: user.email, name, role: 'barber',
            shopId: invite.shopId, barberId: barberRef.id, createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'invites', invite.id), { used: true, usedBy: user.uid, usedAt: serverTimestamp() });
        navigate(`/barber/${invite.shopId}/${barberRef.id}/dashboard`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const name = data.get('name'), email = data.get('email'), password = data.get('password');
        if (!name || !email || !password) { setError('Please fill in all fields.'); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
        setError(''); setLoading(true);
        let user = null;
        try {
            const userCred = await createUserWithEmailAndPassword(auth, email, password);
            user = userCred.user;
            await completeSignup(user, name);
        } catch (err) {
            if (user) { try { await user.delete(); } catch (_) {} }
            setError(err.code === 'auth/email-already-in-use' ? 'This email is already registered.' : err.message);
        }
        setLoading(false);
    };

    const handleGoogle = async () => {
        setLoading(true); setError('');
        try {
            const result   = await signInWithPopup(auth, new GoogleAuthProvider());
            const user     = result.user;
            const userSnap = await getDoc(doc(db, 'users', user.uid));
            if (userSnap.exists()) {
                const ud = userSnap.data();
                if (ud.role === 'barber' && ud.barberId) { navigate(`/barber/${ud.shopId}/${ud.barberId}/dashboard`); return; }
            }
            await completeSignup(user, user.displayName ?? 'Barber');
        } catch (err) { console.error(err.message); setError('Google sign-in failed. Try again.'); }
        setLoading(false);
    };

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
            <ScissorsBg />

            {/* ── Top bar ── */}
            <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 5 }, pt: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <img src={logo} alt="DigitalQ" style={{ width: 36, height: 'auto' }} />
                    <Typography sx={{ fontFamily: typography.serif, fontSize: 16, fontWeight: 700, color: colors.primary }}>DigitalQ</Typography>
                </Box>
                <Typography onClick={() => navigate('/signin')} sx={{ fontSize: 13, color: colors.textFaint, cursor: 'pointer', '&:hover': { color: colors.primary } }}>
                    Sign in →
                </Typography>
            </Box>

            {/* ── Hero + form ── */}
            <Box sx={{
                position: 'relative', zIndex: 1,
                minHeight: 'calc(100vh - 64px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 },
                gap: { xs: 0, md: 10 },
                flexDirection: { xs: 'column', md: 'row' },
            }}>

                {/* ── Left hero copy (desktop) ── */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', maxWidth: 400, flex: 1 }}>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1,
                        bgcolor: `${colors.primary}18`, border: `1px solid ${colors.primary}44`,
                        borderRadius: 100, px: 1.5, py: 0.5, mb: 3, width: 'fit-content',
                    }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.primary, boxShadow: `0 0 6px ${colors.primary}` }} />
                        <Typography sx={{ fontSize: 12, color: colors.primary, fontWeight: 600, letterSpacing: 0.5 }}>Barber Onboarding</Typography>
                    </Box>

                    <Typography sx={{ fontFamily: typography.serif, fontSize: { md: 38, lg: 46 }, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1, mb: 2 }}>
                        Join your shop.<br />
                        <Box component="span" sx={{ color: colors.primary }}>Start cutting.</Box>
                    </Typography>

                    <Typography sx={{ fontSize: 15, color: colors.textFaint, lineHeight: 1.8, mb: 5, maxWidth: 340 }}>
                        Your shop owner sent you an invite code. Enter it to set up your barber profile and start managing your queue today.
                    </Typography>

                    {/* Benefit pills */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {BENEFITS.map((b, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                                    bgcolor: `${colors.primary}12`, border: `1px solid ${colors.primary}30`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 16,
                                }}>{b.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, lineHeight: 1.2 }}>{b.title}</Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.textFaint, lineHeight: 1.5 }}>{b.desc}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Right: form card ── */}
                <Box sx={{ width: '100%', maxWidth: 420, flex: { xs: '1', md: 'none' } }}>

                    {/* Mobile logo */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
                        <img src={logo} alt="DigitalQ" style={{ width: 64, height: 'auto' }} />
                    </Box>

                    {/* ── STEP 1: Code entry ── */}
                    {step === 1 && (
                        <Box sx={{
                            bgcolor: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: `0 24px 60px rgba(0,0,0,0.3)`,
                        }}>
                            {/* Card header band */}
                            <Box sx={{
                                px: 3, py: 2.5,
                                background: `linear-gradient(135deg, ${colors.primary}22 0%, ${colors.primary}08 100%)`,
                                borderBottom: `1px solid ${colors.border}`,
                                display: 'flex', alignItems: 'center', gap: 1.5,
                            }}>
                                <Box sx={{ fontSize: 28 }}>💈</Box>
                                <Box>
                                    <Typography sx={{ fontFamily: typography.serif, fontSize: 18, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}>
                                        Barber Invite
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.textFaint }}>Enter your code to get started</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                {/* Code input */}
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 1, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Invite Code</Typography>
                                    <TextField
                                        fullWidth
                                        value={codeInput}
                                        onChange={e => { setCodeInput(e.target.value.toUpperCase()); setCodeError(''); }}
                                        onKeyDown={e => e.key === 'Enter' && verifyCode()}
                                        size="small"
                                        placeholder="e.g. AB12CD34"
                                        inputProps={{ style: { letterSpacing: 6, fontFamily: 'monospace', fontSize: 20, textAlign: 'center', fontWeight: 700 } }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: colors.bgDeep, borderRadius: 2, color: colors.textPrimary,
                                                '& fieldset': { borderColor: codeError ? colors.error : colors.border },
                                                '&:hover fieldset': { borderColor: colors.primaryBorder },
                                                '&.Mui-focused fieldset': { borderColor: colors.primary },
                                            },
                                            '& input::placeholder': { opacity: 0.3, letterSpacing: 2, fontSize: 15 },
                                        }}
                                    />
                                    {codeError && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                            <ErrorOutline sx={{ fontSize: 13, color: colors.error }} />
                                            <Typography sx={{ fontSize: 12, color: colors.error }}>{codeError}</Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Button fullWidth variant="contained" onClick={() => verifyCode()} disabled={codeLoading}
                                    sx={{ ...btnPrimary, py: 1.3, fontSize: 14, borderRadius: 2 }}>
                                    {codeLoading
                                        ? <CircularProgress size={18} sx={{ color: colors.bgDeep }} />
                                        : <>Verify Code <Box component="span" sx={{ ml: 0.5 }}>→</Box></>
                                    }
                                </Button>

                                <Divider sx={{ borderColor: colors.border }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: 13, color: colors.textFaint }}>
                                        Shop owner?{' '}
                                        <Typography component="span" onClick={() => navigate('/signup/barber')}
                                            sx={{ color: colors.primary, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                                            Register your shop
                                        </Typography>
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: colors.textFaint }}>
                                        Have an account?{' '}
                                        <Typography component="span" onClick={() => navigate('/signin')}
                                            sx={{ color: colors.primary, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                                            Sign in
                                        </Typography>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* ── STEP 2: Create account ── */}
                    {step === 2 && invite && (
                        <Box sx={{
                            bgcolor: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: `0 24px 60px rgba(0,0,0,0.3)`,
                        }}>
                            {/* Success band */}
                            <Box sx={{
                                px: 3, py: 2,
                                bgcolor: `${colors.success}14`,
                                borderBottom: `1px solid ${colors.successBorder}`,
                                display: 'flex', alignItems: 'center', gap: 1.5,
                            }}>
                                <CheckCircle sx={{ fontSize: 20, color: colors.success, flexShrink: 0 }} />
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: colors.success, fontWeight: 600 }}>Valid invite code</Typography>
                                    <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                                        Joining{' '}
                                        <Typography component="span" sx={{ color: colors.primary, fontWeight: 700, fontSize: 13 }}>
                                            {invite.shopName}
                                        </Typography>
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                                <Typography sx={{ fontFamily: typography.serif, fontSize: 20, fontWeight: 700, color: colors.textPrimary, mb: 0.4 }}>
                                    Set up your profile
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 3 }}>
                                    Create your barber account to access the dashboard
                                </Typography>

                                {/* Google first */}
                                <Button fullWidth variant="outlined" onClick={handleGoogle} disabled={loading} startIcon={<GoogleIcon />}
                                    sx={{
                                        borderColor: colors.border, color: colors.textPrimary,
                                        textTransform: 'none', fontSize: 14, borderRadius: 2, py: 1.2,
                                        mb: 2.5,
                                        '&:hover': { borderColor: colors.primaryBorder, bgcolor: `${colors.primary}08` },
                                    }}>
                                    Continue with Google
                                </Button>

                                <Divider sx={{ borderColor: colors.border, mb: 2.5 }}>
                                    <Typography sx={{ fontSize: 11, color: colors.textFaintest, px: 1.5 }}>or use email</Typography>
                                </Divider>

                                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Full Name</Typography>
                                        <TextField name="name" fullWidth required size="small" placeholder="Your name" sx={inputSx} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Email</Typography>
                                        <TextField name="email" fullWidth required size="small" type="email" placeholder="you@example.com" sx={inputSx} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Password</Typography>
                                        <TextField name="password" fullWidth required size="small" type={showPass ? 'text' : 'password'} placeholder="Min. 6 characters" sx={inputSx}
                                            InputProps={{ endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPass(s => !s)} edge="end" size="small" sx={{ color: colors.textFaintest }}>
                                                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )}}
                                        />
                                    </Box>

                                    {error && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1.5, bgcolor: `${colors.error}12`, border: `1px solid ${colors.errorBorder}`, borderRadius: 1.5 }}>
                                            <ErrorOutline sx={{ fontSize: 14, color: colors.error, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: 12, color: colors.error }}>{error}</Typography>
                                        </Box>
                                    )}

                                    <Button type="submit" fullWidth variant="contained" disabled={loading}
                                        sx={{ ...btnPrimary, py: 1.3, fontSize: 14, borderRadius: 2, mt: 0.5 }}>
                                        {loading
                                            ? <CircularProgress size={18} sx={{ color: colors.bgDeep }} />
                                            : '✂️ Join Shop'
                                        }
                                    </Button>
                                </Box>

                                <Button size="small" onClick={() => { setStep(1); setInvite(null); setError(''); }}
                                    sx={{ mt: 2, color: colors.textFaintest, textTransform: 'none', fontSize: 12, width: '100%', '&:hover': { color: colors.textMuted } }}>
                                    ← Use a different code
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* ── Benefits section ── */}
            <Box ref={benefitsRef} sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 }, borderTop: `1px solid ${colors.border}` }}>
                {/* Section header */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: `${colors.primary}14`, border: `1px solid ${colors.primary}30`, borderRadius: 100, px: 2, py: 0.6, mb: 2 }}>
                        <Typography sx={{ fontSize: 12, color: colors.primary, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Why join DigitalQ?</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: typography.serif, fontSize: { xs: 24, md: 32 }, fontWeight: 700, color: colors.textPrimary, mb: 1 }}>
                        Everything a barber needs
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: colors.textFaint, maxWidth: 460, mx: 'auto', lineHeight: 1.7 }}>
                        The best platform for professional barbers to manage their schedule and grow their reputation.
                    </Typography>
                </Box>

                {/* Benefit cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2.5, maxWidth: 1000, mx: 'auto' }}>
                    {BENEFITS.map((b, i) => (
                        <Box key={i} sx={{
                            bgcolor: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 2.5, p: 2.5,
                            transition: 'border-color .2s, transform .2s',
                            '&:hover': { borderColor: colors.primaryBorder, transform: 'translateY(-3px)' },
                            position: 'relative', overflow: 'hidden',
                        }}>
                            {/* Top accent */}
                            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, bgcolor: colors.primary, opacity: 0.5 }} />
                            <Box sx={{
                                width: 44, height: 44, borderRadius: 2, mb: 2,
                                bgcolor: `${colors.primary}14`, border: `1px solid ${colors.primary}28`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 20,
                            }}>{b.icon}</Box>
                            <Typography sx={{ fontFamily: typography.serif, fontSize: 15, fontWeight: 700, color: colors.textPrimary, mb: 0.8 }}>{b.title}</Typography>
                            <Typography sx={{ fontSize: 13, color: colors.textFaint, lineHeight: 1.7 }}>{b.desc}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Bottom CTA */}
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                    <Typography sx={{ fontSize: 13, color: colors.textFaintest, mb: 2 }}>Already have an invite code?</Typography>
                    <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} variant="contained"
                        sx={{ ...btnPrimary, fontSize: 14, px: 4, py: 1.3, borderRadius: 2 }}>
                        Enter Invite Code →
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default JoinShopPage;